import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

function Analysis({ players, gameweekPlayers, teams, positions }) {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [clickedCoordinates, setClickedCoordinates] = useState(null);
  const [playersAtClickedPoint, setPlayersAtClickedPoint] = useState([]);

  // Classify players based on games played (using minutes > 0 as indicator)
  const classifyPlayers = (players, gameweekPlayers) => {
    // Calculate dynamic thresholds based on completed gameweeks
    const gameweeks = [...new Set(gameweekPlayers.map(p => p.gameweek_id).filter(Boolean))];
    const completedGames = gameweeks.length; // Count of distinct gameweeks
    
    // Calculate games played for each player from gameweek_players data
    // A player "played" if they had minutes > 0 in that gameweek
    const gamesPlayedByPlayer = {};
    gameweekPlayers.forEach(player => {
      const playerId = player.fpl_id;
      const minutes = parseInt(player.minutes) || 0;
      
      if (minutes > 0) { // Only count as "played" if they had minutes
        if (!gamesPlayedByPlayer[playerId]) {
          gamesPlayedByPlayer[playerId] = new Set();
        }
        gamesPlayedByPlayer[playerId].add(player.gameweek_id);
      }
    });
    
    // Calculate thresholds as percentages of completed games
    const maxGames = completedGames;
    const regularStarterThreshold = Math.ceil(maxGames * 0.8); // 80% of completed games
    const bencherThreshold = Math.ceil(maxGames * 0.6); // 60% of completed games
    const backbencherThreshold = Math.ceil(maxGames * 0.3); // 30% of completed games
    
    console.log(`📊 Classification thresholds (${completedGames} completed games):`, {
      maxGames,
      regularStarterThreshold,
      bencherThreshold,
      backbencherThreshold
    });
    
    return players.map(player => {
      const gamesPlayed = gamesPlayedByPlayer[player.fpl_id] ? gamesPlayedByPlayer[player.fpl_id].size : 0;
      let category = 'benchwarmers';
      
      if (gamesPlayed >= regularStarterThreshold && gamesPlayed <= maxGames) {
        category = 'regular_starters';
      } else if (gamesPlayed >= bencherThreshold && gamesPlayed < regularStarterThreshold) {
        category = 'benchers';
      } else if (gamesPlayed >= backbencherThreshold && gamesPlayed < bencherThreshold) {
        category = 'backbenchers';
      } else if (gamesPlayed >= 1 && gamesPlayed < backbencherThreshold) {
        category = 'benchwarmers';
      }
      
      return {
        ...player,
        category,
        gamesPlayed,
        playerName: `${player.first_name} ${player.second_name}`,
        price: parseFloat(player.now_cost) / 10,
        points: parseInt(player.total_points) || 0
      };
    });
  };

  useEffect(() => {
    if (players && players.length > 0 && gameweekPlayers && gameweekPlayers.length > 0) {
      const classifiedPlayers = classifyPlayers(players, gameweekPlayers);
      setFilteredPlayers(classifiedPlayers);
    }
  }, [players, gameweekPlayers]);

  const getCategoryPlayers = (category) => {
    let players = filteredPlayers;
    
    if (category !== 'all') {
      players = players.filter(player => player.category === category);
    }
    
    if (selectedPosition !== 'all') {
      players = players.filter(player => player.position_id === parseInt(selectedPosition));
    }
    
    return players;
  };

  const getCategoryCounts = () => {
    const counts = {
      regular_starters: 0,
      benchers: 0,
      backbenchers: 0,
      benchwarmers: 0
    };
    
    filteredPlayers.forEach(player => {
      counts[player.category]++;
    });
    
    return counts;
  };

  const categoryCounts = getCategoryCounts();
  const displayPlayers = getCategoryPlayers(selectedCategory);
  
  // Debug: Check for duplicates in displayPlayers
  const displayFplIds = displayPlayers.map(p => p.fpl_id);
  const uniqueDisplayIds = new Set(displayFplIds);
  
  if (displayFplIds.length !== uniqueDisplayIds.size) {
    console.warn('DUPLICATE PLAYERS IN DISPLAY!');
    const duplicates = displayFplIds.filter((id, index) => displayFplIds.indexOf(id) !== index);
    console.log('Duplicate FPL IDs in display:', [...new Set(duplicates)]);
    
    // Show which players are duplicated
    const duplicatePlayers = displayPlayers.filter(player => 
      displayFplIds.filter(id => id === player.fpl_id).length > 1
    );
    console.log('Duplicate players details:', duplicatePlayers.map(p => ({
      name: p.playerName,
      fpl_id: p.fpl_id,
      category: p.category,
      position_id: p.position_id
    })));
  }
  
  // Handle chart click
  const handleChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const element = elements[0];
      
      // Safely access the data point
      let dataPoint;
      if (element.raw && typeof element.raw === 'object') {
        dataPoint = element.raw;
      } else if (element.index !== undefined && element.datasetIndex !== undefined) {
        // Fallback: get data from chart data
        dataPoint = chartData.datasets[element.datasetIndex].data[element.index];
      } else {
        return;
      }
      
      if (!dataPoint || dataPoint.x === undefined || dataPoint.y === undefined) {
        return;
      }
      
      // Get players at specific coordinates
      const playersAtPoint = displayPlayers.filter(player => 
        Math.abs(player.price - dataPoint.x) < 0.1 && Math.abs(player.points - dataPoint.y) < 0.1
      );
      
      setClickedCoordinates({ x: dataPoint.x, y: dataPoint.y });
      setPlayersAtClickedPoint(playersAtPoint);
    }
  };

  // Debug: Log function definition
  // console.log('🔧 handleChartClick function defined:', typeof handleChartClick);
  // console.log('🔧 handleChartClick function:', handleChartClick);

  // Get unique positions for filter
  const uniquePositions = Object.entries(positions).map(([id, name]) => ({
    id: parseInt(id),
    name: name
  }));
  
  // Debug: Check positions object
  // console.log('Positions object:', positions);
  // console.log('Unique positions:', uniquePositions);
  // console.log('Positions object keys:', Object.keys(positions));
  // console.log('Positions object values:', Object.values(positions));

  // Calculate median values for quadrant lines
  const medianPrice = displayPlayers.length > 0 ? 
    displayPlayers.sort((a, b) => a.price - b.price)[Math.floor(displayPlayers.length / 2)].price : 0;
  const medianPoints = displayPlayers.length > 0 ? 
    displayPlayers.sort((a, b) => a.points - b.points)[Math.floor(displayPlayers.length / 2)].points : 0;

  // Prepare data for ROI scatter plot
  const chartData = {
    datasets: [
      {
        label: 'Players',
        data: displayPlayers.map((player, index) => ({
          x: player.price,
          y: player.points,
          playerName: player.playerName,
          team: teams[player.team_code]?.name || player.team_code,
          position: positions[player.position_id] || 'N/A',
          category: player.category,
          fpl_id: player.fpl_id,
          index: index
        })),
        backgroundColor: displayPlayers.map(player => {
          switch (player.category) {
            case 'regular_starters': return 'rgba(34, 197, 94, 0.7)';
            case 'benchers': return 'rgba(59, 130, 246, 0.7)';
            case 'backbenchers': return 'rgba(245, 158, 11, 0.7)';
            case 'benchwarmers': return 'rgba(239, 68, 68, 0.7)';
            default: return 'rgba(156, 163, 175, 0.7)';
          }
        }),
        borderColor: displayPlayers.map(player => {
          switch (player.category) {
            case 'regular_starters': return 'rgb(34, 197, 94)';
            case 'benchers': return 'rgb(59, 130, 246)';
            case 'backbenchers': return 'rgb(245, 158, 11)';
            case 'benchwarmers': return 'rgb(239, 68, 68)';
            default: return 'rgb(156, 163, 175)';
          }
        }),
        borderWidth: 1,
        pointRadius: 6,
        pointHoverRadius: 8
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest',
      axis: 'xy',
      intersect: false
    },
    onClick: (event, elements, chart) => {
      handleChartClick(event, elements);
    },
    plugins: {
      title: {
        display: true,
        text: 'ROI Index: Player Points vs Price',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: false
      },
      legend: {
        display: false
      },
      annotation: {
        annotations: {
          // Vertical line for median price
          verticalLine: {
            type: 'line',
            xMin: medianPrice,
            xMax: medianPrice,
            borderColor: 'rgba(0, 0, 0, 0.3)',
            borderWidth: 1,
            borderDash: [5, 5]
          },
          // Horizontal line for median points
          horizontalLine: {
            type: 'line',
            yMin: medianPoints,
            yMax: medianPoints,
            borderColor: 'rgba(0, 0, 0, 0.3)',
            borderWidth: 1,
            borderDash: [5, 5]
          },
          // Quadrant labels
          q1Label: {
            type: 'label',
            xValue: medianPrice + (medianPrice * 0.25),
            yValue: medianPoints + (medianPoints * 0.25),
            content: ['High Price', 'High Points'],
            font: {
              size: 12,
              weight: 'bold'
            },
            color: 'rgba(0, 0, 0, 0.7)'
          },
          q2Label: {
            type: 'label',
            xValue: medianPrice - (medianPrice * 0.25),
            yValue: medianPoints + (medianPoints * 0.25),
            content: ['Low Price', 'High Points'],
            font: {
              size: 12,
              weight: 'bold'
            },
            color: 'rgba(0, 0, 0, 0.7)'
          },
          q3Label: {
            type: 'label',
            xValue: medianPrice - (medianPrice * 0.25),
            yValue: medianPoints - (medianPoints * 0.25),
            content: ['Low Price', 'Low Points'],
            font: {
              size: 12,
              weight: 'bold'
            },
            color: 'rgba(0, 0, 0, 0.7)'
          },
          q4Label: {
            type: 'label',
            xValue: medianPrice + (medianPrice * 0.25),
            yValue: medianPoints - (medianPoints * 0.25),
            content: ['High Price', 'Low Points'],
            font: {
              size: 12,
              weight: 'bold'
            },
            color: 'rgba(0, 0, 0, 0.7)'
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Player Price (£m)',
          font: {
            weight: 'bold'
          }
        },
        ticks: {
          callback: function(value) {
            return '£' + value + 'm';
          }
        },
        grid: {
          display: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Total Points',
          font: {
            weight: 'bold'
          }
        },
        grid: {
          display: false
        }
      }
    }
  };

  return (
    <div className="analysis-container">
      <h2>Player Analysis</h2>
      
      {/* Category Filter */}
      <div className="category-filter">
        <h3>Player Classification</h3>
        <div className="category-buttons">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          >
            All Players ({filteredPlayers.length})
          </button>
          <button
            onClick={() => setSelectedCategory('regular_starters')}
            className={`category-btn ${selectedCategory === 'regular_starters' ? 'active' : ''}`}
          >
            Regular Players ({categoryCounts.regular_starters})
          </button>
          <button
            onClick={() => setSelectedCategory('benchers')}
            className={`category-btn ${selectedCategory === 'benchers' ? 'active' : ''}`}
          >
            Benchers ({categoryCounts.benchers})
          </button>
          <button
            onClick={() => setSelectedCategory('backbenchers')}
            className={`category-btn ${selectedCategory === 'backbenchers' ? 'active' : ''}`}
          >
            Backbenchers ({categoryCounts.backbenchers})
          </button>
          <button
            onClick={() => setSelectedCategory('benchwarmers')}
            className={`category-btn ${selectedCategory === 'benchwarmers' ? 'active' : ''}`}
          >
            Benchwarmers ({categoryCounts.benchwarmers})
          </button>
        </div>
        
        {/* Position Filter */}
        <div className="position-filter">
          <label htmlFor="position-select" style={{ fontWeight: 'bold', marginRight: '10px' }}>
            Filter by Position:
          </label>
          <select
            id="position-select"
            value={selectedPosition}
            onChange={(e) => setSelectedPosition(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="all">All Positions</option>
            {uniquePositions.map(position => (
              <option key={position.id} value={position.id}>
                {position.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="category-legend">
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: 'rgba(34, 197, 94, 0.7)'}}></span>
            <span>Regular Starters (80%+ games played)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: 'rgba(59, 130, 246, 0.7)'}}></span>
            <span>Benchers (60-79% games played)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: 'rgba(245, 158, 11, 0.7)'}}></span>
            <span>Backbenchers (30-59% games played)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{backgroundColor: 'rgba(239, 68, 68, 0.7)'}}></span>
            <span>Benchwarmers (0-29% games played)</span>
          </div>
        </div>
      </div>

      {/* ROI Chart */}
      <div className="chart-container">
        <div style={{ height: '500px', width: '100%' }}>
          <Scatter 
            data={chartData} 
            options={chartOptions} 
            onClick={handleChartClick}
          />
        </div>
      </div>

      {/* Players in Selected Category */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-4">
          Players in Selected Category
          {clickedCoordinates && playersAtClickedPoint.length > 0 && (
            <span className="ml-2 text-sm text-blue-600">
              (Showing {playersAtClickedPoint.length} players at clicked point)
            </span>
          )}
        </h3>
        
        {clickedCoordinates && playersAtClickedPoint.length > 0 && (
          <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
            📍 £{clickedCoordinates.x}m, {clickedCoordinates.y} pts
            <button 
              onClick={() => {
                setClickedCoordinates(null);
                setPlayersAtClickedPoint([]);
              }}
              className="ml-2 px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded"
            >
              ✕ Clear
            </button>
          </div>
        )}
        
        <div className="player-list">
          <div className="player-grid">
            {(clickedCoordinates && playersAtClickedPoint.length > 0 ? playersAtClickedPoint : displayPlayers).map(player => (
              <div key={player.id} className="player-card">
                <h4>{player.playerName}</h4>
                <p><strong>Team:</strong> {teams[player.team_code]?.name || player.team_code}</p>
                <p><strong>Position:</strong> {positions[player.position_id] || 'N/A'}</p>
                <p><strong>Price:</strong> £{player.price}m</p>
                <p><strong>Points:</strong> {player.points}</p>
                <p><strong>Starts:</strong> {player.starts}</p>
                <p><strong>Category:</strong> {player.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analysis; 